import { FC, useMemo } from "react";
import { FieldFormV2, FieldV2, LoadingV2, useFieldSchemaV2FactoryContext } from "@ucl/library";
import { FIELD_KEYS } from "@/constants";
import "./JointAccountOwnerIdentificationDocumentsForm.scss";
import { FinancialApplicationFormSectionContextType, FinancialApplicationFormValidationDisplayBehavior } from "@/hooks/useFinancialApplicationFormSectionValidation";
import { JointAccountOwner } from "@/types/JointAccountOwner";
import { createJointAccountOwnerFormBuilder } from "../JointAccountOwners/JointAccountOwnerFormBuilder";
import { useJointAccountOwnerDocuments } from "@/hooks/useJointAccountOwnerDocuments";
import { EntityTypes } from "@/constants/entityTypes";
import { GovernmentIdType } from "@/types/enums";

export interface JointAccountOwnerIdentificationDocumentsFormProps {
  jointOwnerId: string;
  autoValidationBehavior?: FinancialApplicationFormValidationDisplayBehavior;
  availableFields?: string[];
  showHeader?: boolean;
  validationContextType: FinancialApplicationFormSectionContextType;
  governmentIdType?: GovernmentIdType;
}

export const JointAccountOwnerIdentificationDocumentsForm: FC<
  JointAccountOwnerIdentificationDocumentsFormProps
> = (props) => {
  const { formValue, setFormValue, saveIdentityDocuments } = useJointAccountOwnerDocuments(
    props.jointOwnerId
  );

  const { schemaFactory } = useFieldSchemaV2FactoryContext();

  const formBuilder = useMemo(() => {
    if (!schemaFactory) return undefined;
    return createJointAccountOwnerFormBuilder(schemaFactory, props.validationContextType);
  }, [schemaFactory]);

  if (!schemaFactory || !formBuilder || !formValue) {
    return (
      <section className="joint-account-owner-identity-documents-form">
        <LoadingV2 />
      </section>
    );
  }

  return (
    <div className="joint-account-owner-identity-documents-form">
      <FieldFormV2<JointAccountOwner>
        value={formValue}
        fieldSchemaFormBuilder={formBuilder}
        onFormSubmit={async (value) => {
          await saveIdentityDocuments(value);
        }}
        onFieldChanged={(newValue, newFieldValue) => {
          setFormValue(newValue, newFieldValue);
        }}
        availableFields={props.availableFields}
      >
        <FieldV2
          fieldProps={FIELD_KEYS.JOINT_ACCOUNT_OWNER.IDENTIFICATION_DOCUMENTS}
          complexFieldValue={{
            entityId: props.jointOwnerId,
            entityType: EntityTypes.FinancialApplication.JointAccountOwner.EntityType,
            governmentIdType: props.governmentIdType,
            showHeader: props.showHeader,
          }}
        />
      </FieldFormV2>
    </div>
  );
};
